import * as _ from 'lodash-es';
import type Scroll from 'quill/blots/scroll';
import React from 'react';
import ExternalBrandLogo from '../../components/ExternalBrandLogo';
import SVGIcon from '../../components/SVGIcon';
import attachmentIcon from '../../icons/non-prefilled/16x16/attachment.svg';
import postIcon from '../../icons/prefilled/16x16/post.svg';
import getFeature, { isFeatureEnabled } from '../../utils/features';
import type { FuzzySearchMatch } from '../../utils/search/fuzzySearch';
import fuzzySearch, { FuzzySearchIndex } from '../../utils/search/fuzzySearch';
import type { HintType } from '../formats/Hint';
import { HINT_LABELS } from '../formats/Hint';
import IFrame from '../formats/IFrame';
import {
  blockquoteIcon,
  boldIcon,
  buttonIcon,
  codeBlockIcon,
  codeIcon,
  dividerIcon,
  equationIcon,
  flowchartIcon,
  h1Icon,
  h2Icon,
  h3Icon,
  hintErrorIcon,
  hintInfoIcon,
  hintSuccessIcon,
  hintWarningIcon,
  iframeIcon,
  imageIcon,
  italicIcon,
  listBulletedIcon,
  listCheckedIcon,
  listNumberedIcon,
  profileIcon,
  strikethroughIcon,
  tableIcon,
} from './editorIcons';

export enum CommandMenuMatchKey {
  Label = 'data.label',
  Keywords = 'data.keywords',
}

export const INSERT_TYPES = {
  IMAGE: 'image',
  ATTACHMENT: 'attachment',
  DIVIDER: 'divider',
  TABLE_EMBED: 'table-embed',
  CODE_BLOCK: 'code-block',
  MENTION_USER: 'mention-user',
  MENTION_POST: 'mention-post',
  FLOWCHART: 'flowchart',
  EQUATION: 'equation',
  BUTTON: 'button',
} as const;

export const Heading1Item = {
  id: 'heading1',
  data: {
    label: 'Heading 1',
    icon: <SVGIcon>{h1Icon}</SVGIcon>,
  },
};

export const Heading2Item = {
  id: 'heading2',
  data: {
    label: 'Heading 2',
    icon: <SVGIcon>{h2Icon}</SVGIcon>,
  },
};

export const Heading3Item = {
  id: 'heading3',
  data: {
    label: 'Heading 3',
    icon: <SVGIcon>{h3Icon}</SVGIcon>,
  },
};

export const BulletedListItem = {
  id: 'bulleted',
  data: {
    label: 'List · Bullet',
    icon: <SVGIcon>{listBulletedIcon}</SVGIcon>,
  },
  tag: 'ul',
};

export const NumberedListItem = {
  id: 'numbered',
  data: {
    label: 'List · Order',
    icon: <SVGIcon>{listNumberedIcon}</SVGIcon>,
  },
  tag: ['ol', 'numbered'],
};

export const ChecklistItem = {
  id: 'checklist',
  data: {
    label: 'List · Check ',
    icon: <SVGIcon>{listCheckedIcon}</SVGIcon>,
  },
};

const CodeBlockItem = {
  id: 'codeblock',
  data: {
    label: 'Code block',
    icon: <SVGIcon>{codeBlockIcon}</SVGIcon>,
  },
};

export const BlockquoteItem = {
  id: 'blockquote',
  data: {
    label: 'Blockquote',
    icon: <SVGIcon>{blockquoteIcon}</SVGIcon>,
  },
};

export const HintWarningItem = {
  id: 'hint-warning',
  data: {
    type: 'warning' as HintType,
    label: `Hint · ${HINT_LABELS.warning}`,
    icon: <SVGIcon>{hintWarningIcon}</SVGIcon>,
  },
  tag: 'yellow',
};

export const HintDangerItem = {
  id: 'hint-danger',
  data: {
    type: 'error' as HintType,
    label: `Hint · ${HINT_LABELS.error}`,
    icon: <SVGIcon>{hintErrorIcon}</SVGIcon>,
  },
  tag: ['red', 'danger'],
};

export const HintSuccessItem = {
  id: 'hint-success',
  data: {
    type: 'success' as HintType,
    label: `Hint · ${HINT_LABELS.success}`,
    icon: <SVGIcon>{hintSuccessIcon}</SVGIcon>,
  },
  tag: 'green',
};

export const HintInfoItem = {
  id: 'hint-info',
  data: {
    type: 'info' as HintType,
    label: `Hint · ${HINT_LABELS.info}`,
    icon: <SVGIcon>{hintInfoIcon}</SVGIcon>,
  },
  tag: ['blue', 'info'],
};

type Item = {
  id: string;
  tag?: string | string[];
  data: {
    label: string;
    keywords?: string[];
    icon: React.ReactNode;
  };
};

type Action = (index: number) => void;

const getSlashCommandMenuSections = ({
  scroll,
  query,
  onFormat,
  onInsert,
  onShowTooltip,
  publicView,
}: {
  scroll: Scroll;
  query: string;
  onFormat: (format: Record<string, boolean | string | number>) => void;
  onInsert: (
    type: (typeof INSERT_TYPES)[keyof typeof INSERT_TYPES],
    index: number,
  ) => void;
  onShowTooltip: (value: string, index: number) => void;
  publicView: boolean;
}) => {
  const actions = new Map<string, (index: number) => void>();
  const bindFormatAction = (
    item: Item,
    format: string,
    value: boolean | string | number,
  ) => {
    if (!scroll.query(format)) return null;
    actions.set(item.id, () => onFormat({ [format]: value }));
    return item;
  };

  const bindAction = (item: Item, action: Action) => {
    actions.set(item.id, action);
    return item;
  };

  const getAction = (id: string) => actions.get(id);

  const thirdPartyItem = ({
    label,
    keywords,
    targetName,
    ...props
  }: {
    label: string;
    keywords?: string[];
    targetName: string;
  } & React.ComponentProps<typeof ExternalBrandLogo>) => {
    const { brand, product } = props;
    const id = ['embed', brand, product].filter(Boolean).join('-');
    return bindAction(
      {
        id,
        data: {
          label,
          keywords,
          icon: <ExternalBrandLogo height={16} {...props} />,
        },
      },
      (index) => onShowTooltip(`Paste a link to ${targetName}`, index),
    );
  };

  const iframeActions =
    window.__slab.PUBLIC_ORG.settings.allowedEmbedHosts &&
    window.__slab.PUBLIC_ORG.settings.allowedEmbedHosts.length > 0 &&
    scroll.query(IFrame.blotName)
      ? [
          bindAction(
            {
              id: 'iframe',
              data: {
                label: 'iframe',
                icon: <SVGIcon>{iframeIcon}</SVGIcon>,
              },
            },
            (index) => {
              onShowTooltip('Paste iframe code to embed', index);
            },
          ),
        ]
      : [];

  const isSupportingCodeBlockItem = scroll.query('code-embed');

  const sections = _.compact([
    {
      label: 'inline formats',
      items: [
        bindFormatAction(
          {
            id: 'bold',
            data: {
              label: 'Bold',
              icon: <SVGIcon>{boldIcon}</SVGIcon>,
            },
          },
          'bold',
          true,
        ),
        bindFormatAction(
          {
            id: 'italic',
            data: {
              label: 'Italic',
              icon: <SVGIcon>{italicIcon}</SVGIcon>,
            },
          },
          'italic',
          true,
        ),
        bindFormatAction(
          {
            id: 'strikethrough',
            data: {
              label: 'Strikethrough',
              icon: <SVGIcon>{strikethroughIcon}</SVGIcon>,
            },
          },
          'strike',
          true,
        ),
        bindFormatAction(
          {
            id: 'inline-code',
            data: {
              label: 'Inline code',
              icon: <SVGIcon>{codeIcon}</SVGIcon>,
            },
          },
          'code',
          true,
        ),
      ],
    },
    {
      label: 'block formats',
      items: [
        bindFormatAction(Heading1Item, 'header', 1),
        bindFormatAction(Heading2Item, 'header', 2),
        bindFormatAction(Heading3Item, 'header', 3),
        bindFormatAction(BulletedListItem, 'list', 'bullet'),
        bindFormatAction(NumberedListItem, 'list', 'ordered'),
        bindFormatAction(ChecklistItem, 'list', 'unchecked'),
        bindFormatAction(HintInfoItem, 'hint', 'info'),
        bindFormatAction(HintSuccessItem, 'hint', 'success'),
        bindFormatAction(HintWarningItem, 'hint', 'warning'),
        bindFormatAction(HintDangerItem, 'hint', 'error'),
        isSupportingCodeBlockItem &&
          bindAction(CodeBlockItem, (index) =>
            onInsert(INSERT_TYPES.CODE_BLOCK, index),
          ),
        bindFormatAction(BlockquoteItem, 'blockquote', true),
      ],
    },
    {
      label: 'embeds',
      items: [
        scroll.query('table-embed') &&
          bindAction(
            {
              id: 'table-embed',
              data: {
                label: 'Table',
                icon: <SVGIcon>{tableIcon}</SVGIcon>,
              },
            },
            (index) => onInsert(INSERT_TYPES.TABLE_EMBED, index),
          ),
        scroll.query('image') &&
          bindAction(
            {
              id: 'image',
              data: {
                label: 'Image',
                icon: <SVGIcon>{imageIcon}</SVGIcon>,
              },
              tag: 'img',
            },
            (index) => onInsert(INSERT_TYPES.IMAGE, index),
          ),
        scroll.query('attachment') &&
          bindAction(
            {
              id: 'file',
              data: {
                label: 'Attachment',
                icon: <SVGIcon>{attachmentIcon}</SVGIcon>,
              },
            },
            (index) => onInsert(INSERT_TYPES.ATTACHMENT, index),
          ),
        isSupportingCodeBlockItem &&
          bindAction(
            {
              id: 'flowchart',
              data: {
                label: 'Flowchart',
                icon: <SVGIcon>{flowchartIcon}</SVGIcon>,
              },
              tag: 'mermaid',
            },
            (index) => onInsert(INSERT_TYPES.FLOWCHART, index),
          ),
        isSupportingCodeBlockItem &&
          bindAction(
            {
              id: 'equation',
              data: {
                label: 'Equation',
                icon: <SVGIcon>{equationIcon}</SVGIcon>,
              },
              tag: ['latex', 'katex', 'tex'],
            },
            (index) => onInsert(INSERT_TYPES.EQUATION, index),
          ),
        scroll.query('hr') &&
          bindAction(
            {
              id: 'divider',
              data: {
                label: 'Divider',
                icon: <SVGIcon>{dividerIcon}</SVGIcon>,
              },
              tag: 'hr',
            },
            (index) => onInsert(INSERT_TYPES.DIVIDER, index),
          ),
        isFeatureEnabled('button_blot') &&
          scroll.query('button') &&
          bindAction(
            {
              id: 'button',
              data: {
                label: 'Button',
                icon: <SVGIcon>{buttonIcon}</SVGIcon>,
              },
            },
            (index) => onInsert(INSERT_TYPES.BUTTON, index),
          ),
      ],
    },
    !publicView && {
      label: 'mentions',
      items: [
        bindAction(
          {
            id: 'mention-user',
            data: {
              label: 'User',
              icon: <SVGIcon>{profileIcon}</SVGIcon>,
            },
          },
          (index) => onInsert(INSERT_TYPES.MENTION_USER, index),
        ),
        bindAction(
          {
            id: 'mention-post',
            data: {
              label: 'Post',
              icon: <SVGIcon>{postIcon}</SVGIcon>,
            },
          },
          (index) => onInsert(INSERT_TYPES.MENTION_POST, index),
        ),
      ],
    },
    !publicView && {
      label: 'integration mentions',
      items: [
        thirdPartyItem({
          brand: 'asana',
          label: 'Asana',
          targetName: 'an Asana task',
        }),
        thirdPartyItem({
          brand: 'clickup',
          label: 'ClickUp',
          targetName: 'a ClickUp task',
        }),
        thirdPartyItem({
          brand: 'shortcut',
          label: 'Shortcut',
          targetName: 'a Shortcut story',
        }),
        thirdPartyItem({
          brand: 'freshdesk',
          label: 'Freshdesk',
          targetName: 'a Freshdesk ticket',
        }),
        thirdPartyItem({
          brand: 'github',
          label: 'GitHub',
          targetName: 'a GitHub issue',
        }),
        thirdPartyItem({
          brand: 'guru',
          label: 'Guru',
          targetName: 'a Guru card',
        }),
        thirdPartyItem({
          brand: 'atlassian',
          label: 'Jira',
          targetName: 'a JIRA issue',
        }),
        thirdPartyItem({
          brand: 'monday',
          label: 'Monday',
          targetName: 'a Monday item',
        }),
        thirdPartyItem({
          brand: 'trello',
          label: 'Trello',
          targetName: 'a Trello card',
        }),
        thirdPartyItem({
          brand: 'wrike',
          label: 'Wrike',
          targetName: 'a Wrike task',
        }),
        thirdPartyItem({
          brand: 'todoist',
          label: 'Todoist',
          targetName: 'a Todoist task',
        }),
        ...(getFeature('zendesk')
          ? [
              thirdPartyItem({
                brand: 'zendesk',
                label: 'Zendesk',
                targetName: 'a Zendesk ticket',
              }),
            ]
          : []),
      ],
    },
    !publicView && {
      label: 'integration embeds',
      items: scroll.query('embed')
        ? [
            thirdPartyItem({
              brand: 'airtable',
              label: 'Airtable',
              targetName: 'an Airtable base',
            }),
            thirdPartyItem({
              brand: 'canva',
              label: 'Canva',
              targetName: 'a Canva design',
            }),
            thirdPartyItem({
              brand: 'coda',
              label: 'Coda',
              targetName: 'a Coda document',
            }),
            thirdPartyItem({
              brand: 'codepen',
              label: 'CodePen',
              targetName: 'a CodePen pen',
            }),
            thirdPartyItem({
              brand: 'drawio',
              label: 'Draw.io',
              targetName: 'a Draw.io diagram',
            }),
            thirdPartyItem({
              brand: 'dropbox',
              label: 'Dropbox',
              targetName: 'an image or video in Dropbox',
            }),
            thirdPartyItem({
              brand: 'figma',
              label: 'Figma',
              targetName: 'a Figma project',
            }),
            thirdPartyItem({
              brand: 'gliffy',
              label: 'Gliffy',
              targetName: 'a Gliffy diagram',
            }),
            thirdPartyItem({
              brand: 'google',
              product: 'calendar',
              label: 'Google Calendar',
              targetName: 'a calendar in Google Calendar',
            }),
            thirdPartyItem({
              brand: 'google',
              product: 'doc',
              label: 'Google Docs',
              targetName: 'a document in Google Docs',
            }),
            thirdPartyItem({
              brand: 'google',
              product: 'drive',
              label: 'Google Drive',
              targetName: 'a Google Drive file',
            }),
            thirdPartyItem({
              brand: 'google',
              product: 'sheet',
              label: 'Google Sheets',
              targetName: 'a spreadsheet in Google Sheets',
            }),
            thirdPartyItem({
              brand: 'google',
              product: 'slide',
              label: 'Google Slides',
              targetName: 'a presentation in Google Slides',
            }),
            thirdPartyItem({
              brand: 'invision',
              label: 'InVision',
              targetName: 'an InVision prototype',
            }),
            thirdPartyItem({
              brand: 'iorad',
              label: 'iorad',
              targetName: 'an Iorad tutorial',
            }),
            thirdPartyItem({
              brand: 'jotform',
              label: 'Jotform',
              targetName: 'a Jotform form',
            }),
            thirdPartyItem({
              brand: 'jsfiddle',
              label: 'JSFiddle',
              targetName: 'a JSFiddle fiddle',
            }),
            thirdPartyItem({
              brand: 'loom',
              label: 'Loom',
              targetName: 'a Loom video',
            }),
            thirdPartyItem({
              brand: 'lucidchart',
              label: 'Lucid',
              targetName: 'a Lucid chart or board',
            }),
            thirdPartyItem({
              brand: 'ludus',
              label: 'Ludus',
              targetName: 'a Ludus Slideshow',
            }),
            thirdPartyItem({
              brand: 'microsoft',
              product: '365',
              label: 'Microsoft 365',
              targetName: 'a document',
              keywords: ['office'],
            }),
            thirdPartyItem({
              brand: 'miro',
              label: 'Miro',
              targetName: 'a Miro whiteboard',
            }),
            thirdPartyItem({
              brand: 'mode',
              label: 'Mode',
              targetName: 'a Mode dashboard',
            }),
            thirdPartyItem({
              brand: 'monday',
              label: 'Monday',
              targetName: 'a Monday board view',
            }),
            thirdPartyItem({
              brand: 'otter',
              label: 'Otter',
              targetName: 'an Otter conversation',
            }),
            thirdPartyItem({
              brand: 'pingboard',
              label: 'Pingboard',
              targetName: 'a Pingboard chart',
            }),
            thirdPartyItem({
              brand: 'pitch',
              label: 'Pitch',
              targetName: 'a Pitch Presentation',
            }),
            thirdPartyItem({
              brand: 'rows',
              label: 'Rows',
              targetName: 'a Rows spreadsheet',
            }),
            thirdPartyItem({
              brand: 'slideshare',
              label: 'SlideShare',
              targetName: 'a SlideShare slide',
            }),
            thirdPartyItem({
              brand: 'smartsheet',
              label: 'Smartsheet',
              targetName: 'a Smartsheet document',
            }),
            thirdPartyItem({
              brand: 'tableau',
              label: 'Tableau',
              targetName: 'a Tableau view',
            }),
            thirdPartyItem({
              brand: 'typeform',
              label: 'Typeform',
              targetName: 'a Typeform form',
            }),
            thirdPartyItem({
              brand: 'vimeo',
              label: 'Vimeo',
              targetName: 'a Vimeo video',
            }),
            thirdPartyItem({
              brand: 'whimsical',
              label: 'Whimsical',
              targetName: 'a Whimsical file',
            }),
            thirdPartyItem({
              brand: 'wistia',
              label: 'Wistia',
              targetName: 'a Wistia video',
            }),
            thirdPartyItem({
              brand: 'twitter',
              label: 'X',
              targetName: 'a tweet',
              keywords: ['twitter'],
            }),
            thirdPartyItem({
              brand: 'google',
              product: 'youtube',
              label: 'Youtube',
              targetName: 'a Youtube video',
            }),
            ...iframeActions,
          ]
        : [...iframeActions],
    },
  ]);

  const filteredSections = sections
    .map((section) => {
      const items = section.items.filter((item): item is Item => !!item);
      const filteredItems: (Item & {
        data: { matches?: FuzzySearchMatch[] };
      })[] = section.label.toUpperCase().startsWith(query)
        ? items
        : fuzzySearch(
            query,
            new FuzzySearchIndex(items, [
              CommandMenuMatchKey.Label,
              CommandMenuMatchKey.Keywords,
              'tag',
            ]),
            {
              weights: {
                [CommandMenuMatchKey.Label]: 1,
                [CommandMenuMatchKey.Keywords]: 0.1,
                tag: 0.01,
              },
            },
          )
            .toSorted((a, b) =>
              a.score === b.score ? a.refIndex - b.refIndex : b.score - a.score,
            )
            .map(({ item: { id, data }, matches }) => ({
              id,
              data: { ...data, matches },
            }));

      return {
        ...section,
        items: filteredItems,
      };
    })
    .filter(({ items }) => items.length > 0);

  return { sections: filteredSections, getAction };
};

export default getSlashCommandMenuSections;
